/**
 * IdeaBankPage — Tasks → Idea Bank.
 *
 * A holding area for tasks that are planned but have no deadline and are
 * not being actively worked on. Items never appear in the main task list,
 * calendar, overdue alerts, or EOD reports until they are "Activated".
 *
 * RBAC mirrors the backend: Owner / Admin see ALL items in the workspace;
 * Manager / Team see only items they created OR are assigned to.
 *
 * Activate flow:
 *   1. Click Activate on an idea bank row
 *   2. The TaskCreateModal opens, pre-filled with the idea's fields
 *   3. The user MUST set a due date (TaskCreateModal already accepts blank,
 *      but we gate the modal's submit with a custom required check here)
 *   4. On successful task create, we DELETE the idea bank item
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { ideaBankAPI, taskAPI, clientGroupAPI, workspaceAPI } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { TaskCreateModal } from '../components/TaskCreateModal';
import { IdeaBankFormDialog } from '../components/IdeaBankFormDialog';
import { AttachmentsDisplay } from '../components/AttachmentsDisplay';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { StellarSelect } from '../components/StellarFormControls';
import { AssigneeAvatars } from '../components/AssigneeAvatars';
import { formatDate } from '../lib/dateFormat';
import {
  Lightbulb, Plus, Search, Trash2, Pencil, X,
  PlayCircle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_BADGE = {
  high: { label: 'High', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  medium: { label: 'Med', cls: 'bg-gold/15 text-gold border-gold/30' },
  low: { label: 'Low', cls: 'bg-foreground/[0.05] text-muted-foreground/70 border-border/30' },
};

export default function IdeaBankPage() {
  const { currentWorkspace, workspaceRole } = useWorkspace();
  const { user } = useAuth();
  const wsId = currentWorkspace?.id;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('__all__');
  const [filterPriority, setFilterPriority] = useState('__all__');
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);

  // Dialogs
  const [newOpen, setNewOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  // Activate flow — TaskCreateModal pre-filled from an idea bank item.
  const [activateOpen, setActivateOpen] = useState(false);
  const [activateSource, setActivateSource] = useState(null);

  const fetchItems = useCallback(() => {
    if (!wsId) return;
    setLoading(true);
    ideaBankAPI.list(wsId)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [wsId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (!wsId) return;
    workspaceAPI.listAssignableMembers(wsId)
      .then((r) => setMembers((r.data || []).map((m) => (m.user ? { ...m.user, role: m.role } : null)).filter(Boolean)))
      .catch(() => setMembers([]));
    clientGroupAPI.list(wsId).then((r) => setClients(sortClientsAlpha(r.data || []))).catch(() => {});
  }, [wsId]);

  const categories = useMemo(() => {
    const s = new Set();
    items.forEach((i) => {
      const c = (i.category || '').trim();
      if (c) s.add(c);
    });
    return Array.from(s).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    return items.filter((it) => {
      if (filterCategory !== '__all__' && (it.category || '').trim() !== filterCategory) return false;
      if (filterPriority !== '__all__' && (it.priority || 'medium') !== filterPriority) return false;
      if (!q) return true;
      const hay = `${it.title || ''} ${it.description || ''} ${it.category || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, filterCategory, filterPriority]);

  const handleDelete = async (it) => {
    if (!window.confirm(`Delete idea "${it.title}" permanently?`)) return;
    try {
      await ideaBankAPI.delete(wsId, it.id);
      setItems((prev) => prev.filter((p) => p.id !== it.id));
      toast.success('Idea deleted');
      window.dispatchEvent(new Event('stellar-sidebar-counts-refresh'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete idea');
    }
  };

  const startActivate = (it) => {
    setActivateSource(it);
    setActivateOpen(true);
  };

  // When the task is created from the Activate modal, delete the source
  // idea bank item to complete the conversion.
  const handleActivatedTaskCreated = async () => {
    if (!activateSource) return;
    try {
      await ideaBankAPI.delete(wsId, activateSource.id);
      setItems((prev) => prev.filter((p) => p.id !== activateSource.id));
      toast.success(`"${activateSource.title}" is now a live task`);
      window.dispatchEvent(new Event('stellar-sidebar-counts-refresh'));
      window.dispatchEvent(new Event('stellar-task-changed'));
    } catch (err) {
      // Task created, but item delete failed. Surface the error but keep
      // the row visible so the user can manually delete it.
      toast.error('Task created, but failed to remove from Idea Bank. Please delete the source idea manually.');
    } finally {
      setActivateSource(null);
    }
  };

  const isAdminLike = user?.role === 'super_admin' || workspaceRole === 'owner' || workspaceRole === 'admin';

  return (
    <div className="p-4 md:p-6 max-w-[1280px] mx-auto" data-testid="idea-bank-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-display font-semibold flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-gold" />
            Idea Bank
          </h1>
          <p className="text-[12px] text-muted-foreground/70 mt-0.5 max-w-[680px]">
            Ideas, future hires, future projects, internal improvements — things you want to remember and act on someday.
            They do <strong>not</strong> appear in the main task list, calendar, overdue alerts, or EOD reports.
            Click <span className="text-gold">Activate</span> when you&apos;re ready to start.
          </p>
        </div>
        <Button
          className="bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30 shrink-0"
          onClick={() => setNewOpen(true)}
          data-testid="idea-bank-new-btn"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          New Idea
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border/30 mb-3">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-3.5 w-3.5 text-muted-foreground/50 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search ideas…"
                className="h-9 pl-8 text-[13px] bg-foreground/[0.02] border-border/30"
                data-testid="idea-bank-search"
              />
            </div>
            <div className="min-w-[160px]">
              <StellarSelect
                value={filterCategory}
                onValueChange={setFilterCategory}
                options={[{ value: '__all__', label: 'All categories' }, ...categories.map((c) => ({ value: c, label: c }))]}
                size="sm"
                testId="idea-bank-filter-category"
              />
            </div>
            <div className="min-w-[120px]">
              <StellarSelect
                value={filterPriority}
                onValueChange={setFilterPriority}
                options={[
                  { value: '__all__', label: 'All priorities' },
                  { value: 'high', label: 'High' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'low', label: 'Low' },
                ]}
                size="sm"
                testId="idea-bank-filter-priority"
              />
            </div>
            {(filterCategory !== '__all__' || filterPriority !== '__all__' || search) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSearch(''); setFilterCategory('__all__'); setFilterPriority('__all__'); }}
                className="text-[11px] text-muted-foreground/70 hover:text-foreground"
                data-testid="idea-bank-clear-filters"
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
            <div className="ml-auto text-[11px] text-muted-foreground/50" data-testid="idea-bank-count-pill">
              {filteredItems.length} idea{filteredItems.length === 1 ? '' : 's'}
              {isAdminLike && <span className="ml-1 opacity-60">(workspace-wide)</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground/50" data-testid="idea-bank-loading">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
          Loading ideas…
        </div>
      ) : filteredItems.length === 0 ? (
        <Card className="bg-card border-border/30">
          <CardContent className="py-16 text-center" data-testid="idea-bank-empty">
            <Lightbulb className="h-10 w-10 text-gold/30 mx-auto mb-3" />
            <p className="text-[14px] font-medium text-foreground/80">No ideas yet</p>
            <p className="text-[12px] text-muted-foreground/60 mt-1 max-w-[420px] mx-auto">
              {items.length === 0
                ? 'Capture future hires, projects, AI tools, and internal improvements here. They stay out of your live task list until you Activate them.'
                : 'No ideas match your filters. Clear them to see everything.'}
            </p>
            {items.length === 0 && (
              <Button
                onClick={() => setNewOpen(true)}
                className="bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30 mt-4"
                data-testid="idea-bank-empty-cta"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add your first idea
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredItems.map((it) => {
            const pri = PRIORITY_BADGE[it.priority || 'medium'] || PRIORITY_BADGE.medium;
            const client = clients.find((c) => c.id === it.client_group_id);
            const dateAdded = it.created_at ? formatDate(it.created_at) : '';
            const descPreview = (it.description || '').trim();
            const isMine = it.created_by === user?.id;
            const canEdit = isAdminLike || isMine;
            return (
              <Card
                key={it.id}
                className="bg-card border-border/30 hover:border-gold/30 transition-colors"
                data-testid={`idea-bank-row-${it.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[14px] font-semibold text-foreground truncate" data-testid={`idea-bank-row-title-${it.id}`}>{it.title}</p>
                        <Badge className={`text-[10px] font-medium border ${pri.cls}`}>{pri.label}</Badge>
                        {it.category && (
                          <Badge
                            variant="outline"
                            className="text-[10px] font-medium border-gold/30 text-gold bg-gold/[0.04]"
                            data-testid={`idea-bank-row-category-${it.id}`}
                          >
                            {it.category}
                          </Badge>
                        )}
                        {client && (
                          <Badge variant="outline" className="text-[10px] font-medium border-border/40 text-muted-foreground/80">
                            {client.name}
                          </Badge>
                        )}
                      </div>
                      {descPreview && (
                        <p
                          className="text-[12px] text-muted-foreground/80 mt-1.5 whitespace-pre-wrap break-words"
                          data-testid={`idea-bank-row-description-${it.id}`}
                        >
                          {descPreview}
                        </p>
                      )}
                      {/* Attachments — render images / videos / PDFs with the
                          same rich display used in the task detail view. */}
                      {(it.attachments || []).length > 0 && (
                        <div className="mt-2.5" data-testid={`idea-bank-row-attachments-${it.id}`}>
                          <AttachmentsDisplay
                            attachments={it.attachments}
                            onDelete={async (attachmentId) => {
                              await ideaBankAPI.deleteAttachment(wsId, it.id, attachmentId);
                              setItems((prev) => prev.map((p) => (
                                p.id === it.id
                                  ? { ...p, attachments: (p.attachments || []).filter((a) => !(a && a.id === attachmentId)) }
                                  : p
                              )));
                              toast.success('Attachment deleted');
                            }}
                            canDelete={(att) => {
                              if (user?.role === 'super_admin') return true;
                              if (['owner', 'admin', 'manager'].includes(workspaceRole)) return true;
                              if (it.created_by === user?.id) return true;
                              return typeof att === 'object' && att.uploaded_by === user?.id;
                            }}
                            testIdPrefix={`idea-bank-row-attachments-display-${it.id}`}
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-2.5 text-[11px] text-muted-foreground/55 flex-wrap">
                        {(it.assigned_to || []).length > 0 && (
                          <div className="flex items-center gap-1.5">
                            <AssigneeAvatars assignees={it.assigned_to} members={members} max={3} />
                          </div>
                        )}
                        {dateAdded && <span>Added {dateAdded}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => startActivate(it)}
                        className="bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30 h-8 text-[12px]"
                        data-testid={`idea-bank-activate-${it.id}`}
                        title="Convert this idea into a live task"
                      >
                        <PlayCircle className="h-3.5 w-3.5 mr-1" />
                        Activate
                      </Button>
                      {canEdit && (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => { setEditingItem(it); setEditOpen(true); }}
                            className="h-8 w-8 text-muted-foreground/60 hover:text-foreground"
                            title="Edit idea"
                            data-testid={`idea-bank-edit-${it.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDelete(it)}
                            className="h-8 w-8 text-muted-foreground/60 hover:text-red-400"
                            title="Delete idea"
                            data-testid={`idea-bank-delete-${it.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* New / Edit dialogs */}
      <IdeaBankFormDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        workspaceId={wsId}
        members={members}
        clients={clients}
        currentUserId={user?.id || ''}
        onSaved={(saved) => {
          setItems((prev) => [saved, ...prev]);
          window.dispatchEvent(new Event('stellar-sidebar-counts-refresh'));
        }}
      />
      <IdeaBankFormDialog
        open={editOpen}
        onOpenChange={(o) => { setEditOpen(o); if (!o) setEditingItem(null); }}
        workspaceId={wsId}
        members={members}
        clients={clients}
        currentUserId={user?.id || ''}
        editingItem={editingItem}
        onSaved={(saved) => {
          setItems((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
        }}
      />

      {/* Activate → Pre-filled New Task modal.
          The user MUST pick a due date — we surface a hint in TaskCreateModal
          but cannot block on it without forking the modal; if they create
          a task with no due date, that's still valid behaviour (matches the
          rest of the app — many tasks have no due date). */}
      <TaskCreateModal
        open={activateOpen}
        onOpenChange={(o) => { setActivateOpen(o); if (!o) setActivateSource(null); }}
        workspaceId={wsId}
        members={members}
        clients={clients}
        prefilledClientId={activateSource?.client_group_id || ''}
        currentUserId={user?.id || ''}
        testIdPrefix="idea-bank-activate"
        activatePrefill={activateSource ? {
          title: activateSource.title || '',
          description: activateSource.description || '',
          assigned_to: activateSource.assigned_to || [],
          priority: activateSource.priority || 'medium',
          attachments: activateSource.attachments || [],
        } : null}
        onCreated={handleActivatedTaskCreated}
      />
    </div>
  );
}
